import React from "react"
import clsx from "clsx"
import { isMobileOnly } from "react-device-detect"
import i18next from "i18next"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"
import { pdf } from "@react-pdf/renderer"
import { useParams } from "react-router-dom"

import ProtectedLayout from "layouts/Protected"
import { privateRoutes } from "router/routes"

import { useAlert, useAuth, useBikeStoreProcess } from "hooks"

import { isNotEmpty } from "utils/validation"
import dateService from "services/date"
import { CaretIcon } from "components/Elements/Icons"
import { FormSearchBike } from "./Partials"
import { InsuranceForm } from "./Partials/Bicytrust"

// PDF
import saveBlob from "utils/saveBlob"
import SummaryBikePDF from "features/bike/Partials/Pdf/SummaryBikePDF"

import { getLabelFromBikeId, BIKE_SOLD, BC_OPERATOR } from "utils/constants"

const updatesVariants = [
	{
		id: "sell-succcess",
		title: i18next.t("sell_done_title", { ns: "bike" }),
		successDescription: i18next.t("sell_success_description", { ns: "bike" }),
	},
]

export const BikeUpdatedPage = ({ variant = updatesVariants[0].id }) => {
	const { bikeProcessState, fetchBike } = useBikeStoreProcess()
	const { currentBike: bike } = bikeProcessState
	const [operatorGender, setOperatorGender] = React.useState(" ")
	const { t } = useTranslation()

	const { slug } = useParams()
	const { alert } = useAlert()
	const { entity } = useAuth()

	const bicytrustEnabled = entity?.entity_data?.est_bicytrust && bike?.operator === BC_OPERATOR

	React.useEffect(() => {
		!bike && fetchBike(slug)
	}, [bike]) // eslint-disable-line

	React.useEffect(() => {
		if (bike?.operator && entity?.data_operators) {
			const gender = entity.data_operators.find((b) => b.operator === bike.operator)
				?.entity_data?.operator_gender

			if (gender && gender !== "undefinded") setOperatorGender(gender)
		}
	}, [bike, entity]) // eslint-disable-line

	// Generate PDF summary
	const viewSummary = React.useCallback(async () => {
		// Manage pdf generation
		const timeStamp = dateService.getCurrentDate("dd-MM-y-HH'h'mm")
		const generateBikePDF =
			bike &&
			operatorGender &&
			(await pdf(
				<SummaryBikePDF bike={bike} entity={entity} operatorGender={operatorGender} />
			)
				.toBlob()
				.then((blob) => {
					saveBlob(blob, `imprimer-recapitulatif-${bike?.code}-${timeStamp}.pdf`, {
						targetFile: false,
					})
					return blob
				})
				.catch((e) => null))

		if (!generateBikePDF) {
			alert("error", t("pdf.pdf_error", { ns: "bike" }))
		}
	}, [bike, entity, operatorGender, alert, t])

	return (
		<ProtectedLayout className="py-5">
			<div className="l-container">
				{bike && (
					<>
						<div className="c-section-title mb-12 px-8">
							<div className="c-section-title__content justify-between">
								<div className="flex items-center justify-start w-full">
									<NavLink
										to={privateRoutes.searchBike.path}
										className="flex"
										aria-label={t("back_to_search", { ns: "bike" })}
									>
										<span className="flex py-2">
											<CaretIcon size={26} />
										</span>
									</NavLink>

									<h1 className="c-section-title__title c-h2 text-primary ml-4">
										{updatesVariants.find((v) => v.id === variant)?.title}
									</h1>
								</div>
							</div>
						</div>

						<div className="px-8">
							<div className="c-card p-5 lg:px-12 lg:py-7 mb-7">
								<div
									className={clsx("flex justify-between items-start", {
										"flex-col": isMobileOnly,
									})}
								>
									<div className="lg:w-1/2 w-full">
										<FormSearchBike readonly={true} isAdmin={false} />
									</div>
									<div
										className={clsx("lg:w-1/2 w-full flex justify-end", {
											"flex-col": isMobileOnly,
										})}
									>
										<button
											data-cy="print-summary"
											type="button"
											className={clsx(
												"c-btn c-btn--primary-bordered px-12 mb-7",
												{ "mt-3": isMobileOnly }
											)}
											onClick={viewSummary}
										>
											{t("cta_print_recap", { ns: "bike" })}
										</button>
									</div>
								</div>
								<p className="mt-7">
									{
										updatesVariants.find((v) => v.id === variant)
											?.successDescription
									}
								</p>
							</div>

							<div className="l-grid u-mg-negative-hz-m mb-7">
								<div className="lg:w-1/2 w-full p-5">
									<div className="c-card h-full p-5 lg:px-12 lg:py-7 mb-7">
										<h2 className="c-h2 font-semibold mb-7">
											{t("description_title", { ns: "bike" })}
										</h2>
										<p className="mb-2">
											<span className="text-black font-bold">
												{t("brand", { ns: "bike" })} :{" "}
											</span>
											<span className="text-primary ml-2">{bike?.brand}</span>
										</p>
										<p className="mb-2">
											<span className="text-black font-bold">
												{t("model", { ns: "bike" })} :{" "}
											</span>
											<span className="text-primary ml-2">{bike?.model}</span>
										</p>
										<p className="mb-2">
											<span className="text-black font-bold">
												{t("type", { ns: "bike" })} :{" "}
											</span>
											<span className="text-primary ml-2">
												{t(`${getLabelFromBikeId(bike?.gear_type_id)}`)}
											</span>
										</p>
										<p className="mb-2">
											<span className="text-black font-bold">
												{t("color", { ns: "bike" })} :{" "}
											</span>
											<span className="text-primary ml-2">{bike?.color}</span>
										</p>
										<p className="mb-7">
											<span className="text-black font-bold">
												{t("label_vae_short", { ns: "bike" })} :{" "}
											</span>
											<span className="text-primary ml-2">
												{bike?.isVAE === "true"
													? t("label_vae_true", { ns: "bike" })
													: t("label_vae_false", { ns: "bike" })}
											</span>
										</p>
										<p className="mb-2">
											<span className="text-black font-bold">
												{t("serial", { ns: "bike" })} :{" "}
											</span>
											<span className="text-primary ml-2">
												{bike?.frame_serial_number}
											</span>
										</p>
										{bike?.isVAE === "true" && (
											<>
												<p className="mb-2">
													<span className="text-black font-bold">
														{t("serial_engine", { ns: "bike" })} :{" "}
													</span>
													<span className="text-primary ml-2">
														{bike?.engine_serial_number}
													</span>
												</p>
												<p className="mb-2">
													<span className="text-black font-bold">
														{t("serial_battery", { ns: "bike" })} :{" "}
													</span>
													<span className="text-primary ml-2">
														{bike?.battery_serial_number}
													</span>
												</p>
											</>
										)}
									</div>
								</div>
								<div className="lg:w-1/2 w-full p-5">
									<div className="c-card h-full p-5 lg:px-12 lg:py-7 mb-7">
										<h2 className="c-h2 font-semibold mb-7">
											{t("owner_title", { ns: "bike" })}
										</h2>
										{parseInt(bike?.bike_owner?.gender?.toString()) === 1 && (
											<>
												<p className="mb-2">
													<span className="text-black font-bold">
														{t("owner.name", { ns: "bike" })} :{" "}
													</span>
													<span className="text-primary ml-2">
														{bike?.bike_owner.last_name}
													</span>
												</p>
												<p className="mb-2">
													<span className="text-black font-bold">
														{t("owner.firstname", { ns: "bike" })} :{" "}
													</span>
													<span className="text-primary ml-2">
														{bike?.bike_owner.first_name}
													</span>
												</p>
											</>
										)}
										{parseInt(bike?.bike_owner?.gender?.toString()) === 2 && (
											<p className="mb-2">
												<span className="text-black font-bold">
													{t("owner.raison_sociale", { ns: "bike" })} :{" "}
												</span>
												<span className="text-primary ml-2">
													{bike?.bike_owner.social_reason}
												</span>
											</p>
										)}

										{bike?.bike_owner?.phone && (
											<p className="mb-2">
												<span className="text-black font-bold">
													{t("owner.phonenumber", { ns: "bike" })} :{" "}
												</span>
												<span className="text-primary ml-2">
													{bike?.bike_owner.phone.replace("+33", "0")}
												</span>
											</p>
										)}

										{bike?.bike_owner?.mail && (
											<p className="mb-2">
												<span className="text-black font-bold">
													{t("owner.mail", { ns: "bike" })} :{" "}
												</span>
												<span
													className={clsx("text-primary", {
														"break-words": isMobileOnly,
														"ml-2": !isMobileOnly,
													})}
												>
													{bike.bike_owner.mail}
												</span>
											</p>
										)}
									</div>
								</div>
							</div>
							{bicytrustEnabled &&
								isNotEmpty(bike.status) &&
								bike.status === BIKE_SOLD && <InsuranceForm />}

							<div className="flex justify-start mt-7 mb-5">
								<NavLink
									data-cy="register-new-bike"
									to={privateRoutes.searchBike.path}
									className="c-btn c-btn--primary px-12 mb-7"
								>
									{t("cta_activate_new_bike", { ns: "bike" })}
								</NavLink>
							</div>
						</div>
					</>
				)}
			</div>
		</ProtectedLayout>
	)
}
